import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, History, KeyRound, LogOut, MonitorSmartphone, ShieldCheck, ShieldOff, UserRoundCheck, UserRoundX } from 'lucide-react';
import { del, downloadGet, get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/dialog';
import { ActionMenu, ActionMenuItem, ActionMenuSeparator, Alert, Badge, Button, Field, Input, SkeletonText } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { generatePassword } from './licence';
import { holdsSuperAdmin, PEOPLE_QUERY_KEY, PERSON_QUERY_KEY } from './types';

export interface ControlUser {
  id: string;
  name: string;
  isActive: boolean;
  is2faEnabled?: boolean;
  roles: readonly { code: string }[];
}

interface UserSession {
  id: string;
  ip: string | null;
  userAgent: string | null;
  signedInAt: string;
  lastSeenAt: string;
  expiresAt: string;
}

interface LoginEntry {
  at: string;
  ip: string | null;
  userAgent: string | null;
  outcome: 'success' | 'failed';
}

type ControlAction = 'deactivate' | 'reactivate' | 'reset-password' | 'force-logout' | 'enable-2fa' | 'reset-2fa' | 'sessions' | 'logins';

function otpOptions(code: string) {
  return code ? { headers: { 'X-Approver-Otp': code } } : {};
}

function useStepUp() {
  const { user: session } = useAuth();
  return Boolean(session?.has2fa) && !session?.impersonation;
}

function useRefreshPeople() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: PERSON_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };
}

function StepUpField({ code, onChange }: { code: string; onChange: (value: string) => void }) {
  return (
    <Field label={tr('admin', 'authenticator_code')} hint={tr('admin', 'step_up_hint')} htmlFor="approver-otp" required>
      <Input
        id="approver-otp"
        dir="ltr"
        inputMode="numeric"
        maxLength={6}
        className="tracking-[0.4em]"
        value={code}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      />
    </Field>
  );
}

function useCodeReady(code: string): boolean {
  const needsCode = useStepUp();
  return !needsCode || code.length === 6;
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  destructive,
  onClose,
  action,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  onClose: () => void;
  action: (code: string) => Promise<void>;
}) {
  const needsCode = useStepUp();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ready = useCodeReady(code);
  const run = useMutation({
    mutationFn: () => action(code),
    onSuccess: onClose,
    onError: (caught) => setError(describeError(caught)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={body}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={run.isPending}>
            {tr('admin', 'cancel')}
          </Button>
          <Button variant={destructive ? 'destructive' : 'default'} loading={run.isPending} disabled={!ready} onClick={() => run.mutate()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {needsCode && <StepUpField code={code} onChange={setCode} />}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: ControlUser; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshPeople();
  const needsCode = useStepUp();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState(generatePassword());
  const [typed, setTyped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const ready = useCodeReady(code) && password.length >= 10;
  const reset = useMutation({
    mutationFn: () => post(`/org/users/${user.id}/reset-password`, { newPassword: password }, otpOptions(code)),
    onSuccess: () => {
      setShown(password);
      toast.success(tr('admin', 'password_reset'), tr('admin', 'tell_x_the_temporary_password', { name: user.name }));
      refresh();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      icon={KeyRound}
      title={tr('admin', 'reset_password_x', { name: user.name })}
      description={tr('admin', 'reset_password_body')}
      footer={
        shown ? (
          <Button onClick={onClose}>{tr('admin', 'close')}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={reset.isPending}>
              {tr('admin', 'cancel')}
            </Button>
            <Button loading={reset.isPending} disabled={!ready} onClick={() => reset.mutate()}>
              {tr('admin', 'reset_password')}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-3">
        {shown ? (
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{tr('admin', 'temporary_password')}</p>
            <p dir="ltr" className="mt-1 select-all break-all font-mono text-sm">{shown}</p>
            <p className="mt-2 text-xs text-muted-foreground">{tr('admin', 'tell_x_the_temporary_password', { name: user.name })}</p>
          </div>
        ) : (
          <>
            <Field label={tr('admin', 'temporary_password')} required error={password.length > 0 && password.length < 10 ? tr('admin', 'reset_password_body') : undefined}>
              <div className="flex gap-2">
                <Input dir="ltr" type="text" value={password} readOnly={!typed} onChange={(e) => setPassword(e.target.value)} />
                {typed ? (
                  <Button type="button" variant="secondary" onClick={() => { setTyped(false); setPassword(generatePassword()); }}>
                    {tr('admin', 'generate')}
                  </Button>
                ) : (
                  <Button type="button" variant="secondary" onClick={() => { setTyped(true); setPassword(''); }}>
                    {tr('admin', 'type_your_own')}
                  </Button>
                )}
              </div>
            </Field>
            {needsCode && <StepUpField code={code} onChange={setCode} />}
          </>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function Enable2faDialog({ user, onClose }: { user: ControlUser; onClose: () => void }) {
  const toast = useToast();
  const refresh = useRefreshPeople();
  const needsCode = useStepUp();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ready = useCodeReady(code);
  const enable = useMutation({
    mutationFn: () => post<{ id: string; name: string; enrolmentRequired: true }>(`/org/users/${user.id}/2fa/enable`, {}, otpOptions(code)),
    onSuccess: () => {
      toast.success(tr('admin', 'two_factor_enabled_for_x', { name: user.name }), tr('admin', 'enable_2fa_body'));
      refresh();
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      icon={ShieldCheck}
      title={tr('admin', 'enable_2fa_x', { name: user.name })}
      description={tr('admin', 'enable_2fa_body')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enable.isPending}>
            {tr('admin', 'cancel')}
          </Button>
          <Button loading={enable.isPending} disabled={!ready} onClick={() => enable.mutate()}>
            {tr('admin', 'enable_2fa')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {needsCode && <StepUpField code={code} onChange={setCode} />}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function SessionsDialog({ user, onClose }: { user: ControlUser; onClose: () => void }) {
  const toast = useToast();
  const needsCode = useStepUp();
  const [code, setCode] = useState('');
  const ready = useCodeReady(code);
  const sessions = useQuery({
    queryKey: ['user-sessions', user.id],
    queryFn: () => get<UserSession[]>(`/org/users/${user.id}/sessions`),
  });
  const revoke = useMutation({
    mutationFn: (sessionId: string) => del(`/org/users/${user.id}/sessions/${sessionId}`, otpOptions(code)),
    onSuccess: () => {
      toast.success(tr('admin', 'session_ended'));
      void sessions.refetch();
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_end_the_session'), describeError(caught)),
  });
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      icon={MonitorSmartphone}
      title={tr('admin', 'sessions_x', { name: user.name })}
      footer={<Button variant="ghost" onClick={onClose}>{tr('admin', 'close')}</Button>}
    >
      <div className="space-y-3">
        {needsCode && (sessions.data?.length ?? 0) > 0 && <StepUpField code={code} onChange={setCode} />}
        {sessions.isLoading && <SkeletonText lines={2} />}
        {sessions.error && <Alert variant="destructive">{describeError(sessions.error)}</Alert>}
        {sessions.data?.length === 0 && <p className="text-sm text-muted-foreground">{tr('admin', 'no_active_sessions')}</p>}
        {(sessions.data ?? []).map((row) => (
          <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2 text-sm">
            <div className="min-w-0">
              <p className="truncate font-medium">{row.userAgent || tr('admin', 'unknown_device')}</p>
              <p className="text-xs text-muted-foreground">
                {tr('admin', 'ip')}: <span dir="ltr">{row.ip ?? '—'}</span> · {tr('admin', 'signed_in')}: {dateTime(row.signedInAt)} · {tr('admin', 'last_seen')}: {dateTime(row.lastSeenAt)}
              </p>
            </div>
            <Button variant="outline" size="sm" disabled={!ready} loading={revoke.isPending && revoke.variables === row.id} onClick={() => revoke.mutate(row.id)}>
              {tr('admin', 'revoke')}
            </Button>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

function LoginHistoryDialog({ user, onClose }: { user: ControlUser; onClose: () => void }) {
  const logins = useQuery({
    queryKey: ['user-logins', user.id],
    queryFn: () => get<LoginEntry[]>(`/org/users/${user.id}/logins`),
  });
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      icon={History}
      title={tr('admin', 'login_history_x', { name: user.name })}
      footer={<Button variant="ghost" onClick={onClose}>{tr('admin', 'close')}</Button>}
    >
      <div className="max-h-[24rem] overflow-y-auto">
        {logins.isLoading && <SkeletonText lines={3} />}
        {logins.error && <Alert variant="destructive">{describeError(logins.error)}</Alert>}
        {logins.data?.length === 0 && <p className="text-sm text-muted-foreground">{tr('admin', 'no_sign_ins_recorded')}</p>}
        {(logins.data?.length ?? 0) > 0 && (
          <table className="w-full text-sm">
            <thead className="text-start text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-1 text-start">{tr('admin', 'when')}</th>
                <th className="py-1 text-start">{tr('admin', 'ip')}</th>
                <th className="py-1 text-start">{tr('admin', 'device')}</th>
                <th className="py-1 text-start">{tr('admin', 'outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {(logins.data ?? []).map((row, index) => (
                <tr key={`${row.at}-${index}`} className="border-t">
                  <td className="py-1 pe-2 whitespace-nowrap">{dateTime(row.at)}</td>
                  <td className="py-1 pe-2" dir="ltr">{row.ip ?? '—'}</td>
                  <td className="max-w-[18rem] truncate py-1 pe-2" title={row.userAgent ?? undefined}>{row.userAgent ?? '—'}</td>
                  <td className="py-1">
                    <Badge variant={row.outcome === 'success' ? 'success' : 'destructive'}>
                      {row.outcome === 'success' ? tr('admin', 'succeeded') : tr('admin', 'failed_2')}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Dialog>
  );
}

export function UserControlsMenu({ user, trigger }: { user: ControlUser; trigger?: ReactNode }) {
  const { can, canWrite, isSuperAdmin, user: session } = useAuth();
  const toast = useToast();
  const refresh = useRefreshPeople();
  const [open, setOpen] = useState<ControlAction | null>(null);

  if (!session || session.id === user.id) return null;
  if (holdsSuperAdmin(user.roles) && !isSuperAdmin) return null;
  const manage = can('user.manage') && canWrite;
  const resetPassword = can('user.reset_password') && canWrite;
  const forceLogout = can('user.force_logout') && canWrite;
  const manage2fa = can('user.manage_2fa') && canWrite && user.is2faEnabled !== undefined;
  const read = can('user.read', 'user.manage');
  if (!manage && !resetPassword && !forceLogout && !manage2fa && !read) return null;

  const close = () => setOpen(null);
  const toggleActive = async (code: string) => {
    if (user.isActive) await del(`/org/users/${user.id}`, otpOptions(code));
    else await post(`/org/users/${user.id}/reactivate`, {}, otpOptions(code));
    toast.success(user.isActive ? tr('admin', 'x_deactivated', { name: user.name }) : tr('admin', 'x_reactivated', { name: user.name }));
    refresh();
  };
  const signOutEverywhere = async (code: string) => {
    const result = await post<{ sessionsEnded: number }>(`/org/users/${user.id}/force-logout`, {}, otpOptions(code));
    toast.success(tr('admin', 'x_signed_out_everywhere', { name: user.name, count: result.sessionsEnded }));
  };
  const reset2fa = async (code: string) => {
    await post(`/org/users/${user.id}/2fa/reset`, {}, otpOptions(code));
    toast.success(tr('admin', 'two_factor_reset_for_x', { name: user.name }));
    refresh();
  };

  return (
    <>
      <ActionMenu label={tr('admin', 'account_actions')} trigger={trigger}>
        {manage && (
          <ActionMenuItem onSelect={() => setOpen(user.isActive ? 'deactivate' : 'reactivate')} icon={user.isActive ? <UserRoundX /> : <UserRoundCheck />} destructive={user.isActive}>
            {user.isActive ? tr('admin', 'deactivate') : tr('admin', 'reactivate')}
          </ActionMenuItem>
        )}
        {resetPassword && (
          <ActionMenuItem onSelect={() => setOpen('reset-password')} icon={<KeyRound />}>
            {tr('admin', 'reset_password')}
          </ActionMenuItem>
        )}
        {forceLogout && (
          <ActionMenuItem onSelect={() => setOpen('force-logout')} icon={<LogOut />}>
            {tr('admin', 'force_sign_out')}
          </ActionMenuItem>
        )}
        {manage2fa && !user.is2faEnabled && (
          <ActionMenuItem onSelect={() => setOpen('enable-2fa')} icon={<ShieldCheck />}>
            {tr('admin', 'enable_2fa')}
          </ActionMenuItem>
        )}
        {manage2fa && user.is2faEnabled && (
          <ActionMenuItem onSelect={() => setOpen('reset-2fa')} icon={<ShieldOff />}>
            {tr('admin', 'reset_2fa')}
          </ActionMenuItem>
        )}
        {(manage || read) && <ActionMenuSeparator />}
        {manage && (
          <ActionMenuItem onSelect={() => setOpen('sessions')} icon={<MonitorSmartphone />}>
            {tr('admin', 'sessions')}
          </ActionMenuItem>
        )}
        {read && (
          <ActionMenuItem onSelect={() => setOpen('logins')} icon={<History />}>
            {tr('admin', 'login_history')}
          </ActionMenuItem>
        )}
      </ActionMenu>

      {open === 'deactivate' && (
        <ConfirmDialog
          title={tr('admin', 'deactivate_x', { name: user.name })}
          body={tr('admin', 'deactivate_body')}
          confirmLabel={tr('admin', 'deactivate')}
          destructive
          onClose={close}
          action={toggleActive}
        />
      )}
      {open === 'reactivate' && (
        <ConfirmDialog
          title={tr('admin', 'reactivate_x', { name: user.name })}
          body={tr('admin', 'reactivate_body')}
          confirmLabel={tr('admin', 'reactivate')}
          onClose={close}
          action={toggleActive}
        />
      )}
      {open === 'force-logout' && (
        <ConfirmDialog
          title={tr('admin', 'force_sign_out_x', { name: user.name })}
          body={tr('admin', 'force_sign_out_body')}
          confirmLabel={tr('admin', 'force_sign_out')}
          destructive
          onClose={close}
          action={signOutEverywhere}
        />
      )}
      {open === 'reset-2fa' && (
        <ConfirmDialog
          title={tr('admin', 'reset_2fa_x', { name: user.name })}
          body={tr('admin', 'reset_2fa_body')}
          confirmLabel={tr('admin', 'reset_2fa')}
          destructive
          onClose={close}
          action={reset2fa}
        />
      )}
      {open === 'reset-password' && <ResetPasswordDialog user={user} onClose={close} />}
      {open === 'enable-2fa' && <Enable2faDialog user={user} onClose={close} />}
      {open === 'sessions' && <SessionsDialog user={user} onClose={close} />}
      {open === 'logins' && <LoginHistoryDialog user={user} onClose={close} />}
    </>
  );
}

export function ExportUsersButton() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadGet('/org/users/export', 'users.csv');
          toast.success(tr('admin', 'users_exported'));
        } catch (caught) {
          toast.error(tr('admin', 'could_not_export_users'), describeError(caught));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Download className="h-4 w-4" aria-hidden />
      {tr('admin', 'export_csv')}
    </Button>
  );
}
